import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Select,
    Option,
    Chip,
    Typography,
    IconButton,
    Spinner
} from "@material-tailwind/react";
import { XMarkIcon, UserPlusIcon } from "@heroicons/react/24/outline";

const TaskAssigneeSelector = ({ taskId, currentAssignees = [], onAssigneeChange }) => {
    const { rootUrl } = require('../../../config/config');
    const [token] = useState(localStorage.getItem('authToken') || '');

    const [users, setUsers] = useState([]);
    const [assignees, setAssignees] = useState(currentAssignees);
    const [selectedUser, setSelectedUser] = useState('');
    const [loading, setLoading] = useState(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        fetchUsers();
    }, []);

    useEffect(() => {
        setAssignees(currentAssignees);
    }, [currentAssignees]);

    const fetchUsers = async () => {
        try {
            const response = await axios.get(
                `${rootUrl}/api/users`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setUsers(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };

    const handleAssignUser = async () => {
        if (!selectedUser || !taskId) return;

        setLoading(true);
        try {
            const response = await axios.post(
                `${rootUrl}/api/tasks/${taskId}/assign`,
                {
                    user_id: selectedUser,
                    assigned_by: 'current_user'
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                const newAssignee = users.find(u => u.user_id === selectedUser);
                if (newAssignee) {
                    const updated = [...assignees, newAssignee];
                    setAssignees(updated);
                    if (onAssigneeChange) onAssigneeChange(updated);
                }
                setSelectedUser('');
            }
        } catch (error) {
            console.error('Error assigning user:', error);
            alert(error.response?.data?.message || 'Failed to assign user');
        } finally {
            setLoading(false);
        }
    };

    const handleUnassignUser = async (userId) => {
        if (!taskId) return;

        setLoading(true);
        try {
            const response = await axios.delete(
                `${rootUrl}/api/tasks/${taskId}/unassign/${userId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                const updated = assignees.filter(a => a.user_id !== userId);
                setAssignees(updated);
                if (onAssigneeChange) onAssigneeChange(updated);
            }
        } catch (error) {
            console.error('Error unassigning user:', error);
            alert('Failed to unassign user');
        } finally {
            setLoading(false);
        }
    };

    const getUserInitials = (name) => {
        if (!name) return 'U';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    const getAvailableUsers = () => {
        const assignedIds = assignees.map(a => a.user_id);
        return users.filter(u => !assignedIds.includes(u.user_id));
    };

    return (
        <div className="space-y-3">
            <Typography variant="small" color="gray" className="font-semibold">
                Assignees
            </Typography>

            {/* Current Assignees */}
            {assignees.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {assignees.map(assignee => (
                        <Chip
                            key={assignee.user_id}
                            value={
                                <div className="flex items-center gap-2">
                                    <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#FF5733] text-[10px] font-semibold text-white">
                                        {getUserInitials(assignee.name || assignee.username)}
                                    </div>
                                    <span>{assignee.name || assignee.username}</span>
                                    <IconButton
                                        size="sm"
                                        variant="text"
                                        className="p-0 hover:bg-transparent"
                                        onClick={() => handleUnassignUser(assignee.user_id)}
                                        disabled={loading}
                                    >
                                        <XMarkIcon className="h-3 w-3" />
                                    </IconButton>
                                </div>
                            }
                            className="bg-blue-50 text-blue-900"
                        />
                    ))}
                </div>
            )}

            {/* Add New Assignee */}
            {taskId && getAvailableUsers().length > 0 && (
                <div className="flex gap-2">
                    <div className="flex-1">
                        <Select
                            label="Add Assignee"
                            value={selectedUser}
                            onChange={(val) => setSelectedUser(val)}
                            disabled={loading}
                        >
                            {getAvailableUsers().map(user => (
                                <Option key={user.user_id} value={user.user_id}>
                                    <div className="flex items-center gap-2">
                                        <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#FF5733] text-[10px] font-semibold text-white">
                                            {getUserInitials(user.name || user.username)}
                                        </div>
                                        {user.name || user.username}
                                    </div>
                                </Option>
                            ))}
                        </Select>
                    </div>
                    <IconButton
                        color="blue"
                        onClick={handleAssignUser}
                        disabled={loading || !selectedUser}
                    >
                        {loading ? (
                            <Spinner className="h-4 w-4" />
                        ) : (
                            <UserPlusIcon className="h-5 w-5" />
                        )}
                    </IconButton>
                </div>
            )}

            {assignees.length === 0 && (
                <Typography variant="small" color="gray" className="text-center py-2">
                    No assignees yet
                </Typography>
            )}
        </div>
    );
};

export default TaskAssigneeSelector;
